const casesData = [
  {
    slug: "enterprise-network-security",
    title: "Enterprise Network Security Deployment",
    category: "Cybersecurity",
    shortDesc:
      "Designed and deployed a multi-layer security architecture for a mid-size logistics firm handling sensitive shipment data across 12 branches.",
    image:
      "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=800",
    overview:
      "A logistics company with 12 nationwide branches needed to protect its centralized shipment database and inter-branch communication against unauthorized access and data breaches.",
    challenge:
      "The legacy flat network had no segmentation — any compromised device could reach the core database. Rogue DHCP servers had caused outages, and there was no centralized logging or intrusion detection. The client required PCI-DSS level compliance for handling payment data.",
    solution:
      "We implemented a tiered security architecture using Cisco Firepower firewalls at the perimeter, inter-VLAN routing with OSPF for efficient path selection, and 802.1X port authentication on all access switches. A SIEM stack (Wazuh + Elastic) was deployed for real-time threat detection. All branch traffic was tunneled through IPsec VPNs back to the central data center.",
    technologies: [
      "Cisco Firepower 2100 Series",
      "Cisco IOS (VLANs / OSPF / IPsec)",
      "Wazuh SIEM",
      "Elastic Stack",
      "802.1X (FreeRADIUS)",
      "Python (automation scripts)",
    ],
    results:
      "99.8% reduction in security incidents within 3 months. Achieved PCI-DSS compliance. Eliminated rogue DHCP and unauthorized access attempts. Centralized logging reduced mean time to detect (MTTD) from days to under 15 minutes.",
  },
  {
    slug: "smart-cctv-surveillance",
    title: "Smart CCTV Surveillance System",
    category: "CCTV Deployment",
    shortDesc:
      "End-to-end IP camera deployment with centralized NVR storage and remote mobile monitoring for a 5-floor retail complex.",
    image:
      "https://images.unsplash.com/photo-1558002038-1055907df827?w=800",
    overview:
      "A mixed-use retail complex with 5 floors and underground parking required a modern surveillance system to replace outdated analog cameras.",
    challenge:
      "The existing analog system provided low-resolution footage, had no remote access, and required on-site DVR maintenance. Management needed live mobile viewing, 30-day retention, and coverage of all blind spots including stairwells and parking.",
    solution:
      "We deployed 48 Hikvision 4MP IP cameras with Power over Ethernet (PoE) connected to a 48-port PoE+ switch. Two Synology NAS units running Surveillance Station provided NVR functionality with RAID-1 redundancy. A dedicated VLAN isolated camera traffic. Remote access was secured via SSL VPN, and the mobile app provided live viewing for security personnel.",
    technologies: [
      "Hikvision 4MP IP Cameras",
      "Synology Surveillance Station",
      "Ubiquiti PoE+ Switches",
      "VLAN segmentation",
      "SSL VPN (OpenVPN)",
      "Cat6a structured cabling",
    ],
    results:
      "100% blind spot coverage. Remote mobile monitoring reduced security response time by 60%. 30-day retention achieved with room for 60+ days of archive. System has been operational with zero unplanned downtime for 18 months.",
  },
  {
    slug: "isp-network-infrastructure",
    title: "ISP Network Infrastructure Design",
    category: "Network Infrastructure",
    shortDesc:
      "Redesigned the core and distribution network for a regional ISP serving 5,000+ subscribers across rural and suburban areas.",
    image:
      "https://images.unsplash.com/photo-1563770660941-10a45f3bdb5e?w=800",
    overview:
      "A growing regional ISP was experiencing frequent outages during peak hours and could not scale beyond its current subscriber base due to a flat, poorly planned backbone.",
    challenge:
      "The core layer consisted of a single L3 switch with no redundancy. A BGP misconfiguration caused route flapping weekly. Last-mile infrastructure used mixed vendor equipment without standardized configs. The NOC had no visibility into link utilization or subscriber signal quality.",
    solution:
      "We introduced a collapsed-core architecture using two Nexus 9300 switches in a vPC pair for redundancy. OSPF replaced static routing, and BGP peering with upstream providers was stabilized with prefix-list filtering. A MikroTik-based monitoring stack with The Dude and Grafana provided real-time visibility. We also standardized CPE configurations and implemented a zero-touch provisioning pipeline using Python scripts.",
    technologies: [
      "Cisco Nexus 9300 (vPC)",
      "OSPF / BGP",
      "MikroTik CCR Routers",
      "The Dude / Grafana",
      "Python (provisioning)",
      "Fiber GPON (last-mile)",
    ],
    results:
      "Peak-hour outages eliminated. Network uptime improved from 98.2% to 99.95%. Subscriber capacity headroom increased by 300%. NOC response time dropped from hours to minutes with real-time dashboards.",
  },
  {
    slug: "cybersecurity-monitoring",
    title: "Cybersecurity Monitoring System",
    category: "Cybersecurity",
    shortDesc:
      "Built a Security Operations Center (SOC)-style monitoring platform for a fintech startup handling mobile money transactions.",
    image:
      "https://images.unsplash.com/photo-1555949963-ff9fe0c870eb?w=800",
    overview:
      "A fintech startup processing mobile money transactions needed a security monitoring system to detect and respond to threats in real time, as required by their PSP license.",
    challenge:
      "With no security team and a small DevOps crew, the startup relied on basic cloud vendor alerts. A simulated red-team exercise revealed a 48-hour gap between intrusion and detection. Compliance auditors demanded SIEM, IDS/IPS, and incident response playbooks.",
    solution:
      "We deployed Suricata IDS/IPS on the network edge and Wazuh agents on all servers. Logs were centralized in an Elasticsearch cluster with Kibana dashboards. Custom correlation rules flagged anomalous transaction patterns. A Slack-integrated alerting pipeline notified the on-call engineer. We also authored incident response playbooks for the top 5 attack scenarios.",
    technologies: [
      "Suricata IDS/IPS",
      "Wazuh (HIDS)",
      "Elasticsearch / Kibana",
      "TheHive (case management)",
      "Python (correlation rules)",
      "Slack API (alerting)",
    ],
    results:
      "Detection time reduced from 48 hours to under 2 minutes. Passed PSP compliance audit on the first attempt. Blocked 12,000+ malicious IPs in the first quarter. Incident response playbooks reduced mean time to respond (MTTR) by 80%.",
  },
  {
    slug: "business-website-suite",
    title: "Business Website Development Suite",
    category: "Web Systems",
    shortDesc:
      "Developed a suite of high-performance React + API websites for a real estate group managing 30+ property brands.",
    image:
      "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800",
    overview:
      "A real estate group with 30+ property brands needed a unified web platform where each brand could have its own custom front-end while sharing a common backend for listings, inquiries, and analytics.",
    challenge:
      "Each brand had a separate WordPress site with different themes, plugins, and hosting — creating a maintenance nightmare. Page load times averaged over 5 seconds. There was no centralized listing management, and lead data was scattered across a dozen spreadsheets.",
    solution:
      "We built a headless CMS backend (Strapi) serving a REST API consumed by individual React front-ends per brand. Shared components (listings, contact forms, mortgage calculator) were published as an npm package. A Node.js microservice handled lead aggregation and CRM integration. All sites were deployed on Vercel with automatic CI/CD.",
    technologies: [
      "React 18 (Next.js for SEO-critical sites)",
      "Strapi (headless CMS)",
      "Node.js (microservices)",
      "Tailwind CSS",
      "Vercel / AWS Lambda",
      "PostgreSQL / Redis",
    ],
    results:
      "Page load time reduced from 5s+ to under 800ms. Centralized lead management increased conversion tracking accuracy by 95%. Development velocity for new brand sites went from 3 months to 2 weeks. Maintenance costs reduced by 70%.",
  },
  {
    slug: "network-automation-tool",
    title: "Network Automation & Configuration Tool",
    category: "Network Infrastructure",
    shortDesc:
      "Built a Python-based automation framework that cut network configuration time by 90% for a multi-campus university network.",
    image:
      "https://images.unsplash.com/photo-1518432031352-d6fc5c10da5a?w=800",
    overview:
      "A university with 4 campuses and 200+ network devices needed to standardize configurations and automate repetitive tasks across its distributed infrastructure.",
    challenge:
      "The IT team of 3 managed configs via manual SSH sessions — a single VLAN addition across all switches took 4+ hours. Configuration drift was rampant, backup files were scattered, and firmware upgrades required weekend-long efforts with high error rates.",
    solution:
      "We developed a Python automation framework using Netmiko for SSH orchestration and Napalm for configuration management. A Git-backed config backup system tracked every change with diff-based alerts. Custom playbooks automated VLAN provisioning, ACL deployment, and firmware upgrades across all vendor devices (Cisco, HP, MikroTik). A simple web dashboard (Flask) provided a click-to-deploy interface.",
    technologies: [
      "Python 3 / Netmiko",
      "Napalm (config management)",
      "Git (version-controlled configs)",
      "Flask (web dashboard)",
      "Cisco / HP / MikroTik devices",
      "SNMPv3 / Grafana (monitoring)",
    ],
    results:
      "VLAN deployment time dropped from 4+ hours to under 3 minutes. Configuration drift eliminated — all 200+ devices now run standardized configs. Firmware upgrades are completed in under 2 hours instead of a full weekend. The automation framework paid for itself in labor savings within 2 months.",
  },
  {
    slug: "digital-forensics-investigation",
    title: "Digital Forensics & Incident Investigation",
    category: "Digital Forensics",
    shortDesc:
      "Investigation of compromised systems, data recovery, and evidence analysis for a financial institution handling sensitive transaction data.",
    image:
      "https://images.unsplash.com/photo-1580752300992-559f8e0734c0?w=800",
    overview:
      "A financial institution detected unusual activity on a critical server handling transaction processing. The internal IT team was unable to determine the scope of compromise, and the regulator required a formal forensic investigation with a detailed incident timeline.",
    challenge:
      "The compromised server had been running for 72 hours post-intrusion before discovery. Logs were not centrally collected, the attacker had deleted key system logs, and the team lacked the tools to perform memory analysis or carve deleted files. There was also a risk of data exfiltration affecting customer PII.",
    solution:
      "We performed a live forensic acquisition of volatile memory using LiMe and collected disk images with Guymager. File system analysis with The Sleuth Kit recovered deleted artifacts, including a malicious DLL and scheduled persistence tasks. Logs from adjacent systems were correlated in Timesketch to reconstruct the attacker's kill chain. A detailed report with timeline, root cause, and remediation steps was delivered to the regulator.",
    techniques: [
      "Incident Reconstruction",
      "File System Analysis",
      "Log Correlation",
      "Malware Artifact Identification",
    ],
    outcomeSummary:
      "Identified breach source and reconstructed attack timeline for mitigation and reporting.",
    technologies: [
      "LiMe (Linux Memory Extractor)",
      "Guymager (Disk Imaging)",
      "The Sleuth Kit (TSK)",
      "Timesketch (Log Analysis)",
      "Volatility 3 (Memory Analysis)",
      "YARA (Malware Identification)",
    ],
    results:
      "Full attack timeline reconstructed from initial access to data exfiltration attempt. Recovered 14 deleted files, identified 3 persistence mechanisms, and confirmed zero customer PII exposure. The forensic report was accepted by the regulator with no follow-up findings.",
  },
  {
    slug: "proactive-threat-hunting",
    title: "Proactive Threat Hunting Operations",
    category: "Threat Hunting",
    shortDesc:
      "Proactive detection of hidden threats in a healthcare enterprise network handling sensitive patient records across 8 facilities.",
    image:
      "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800",
    overview:
      "A healthcare provider with 8 facilities and 3,000 endpoints engaged us after a near-miss ransomware incident. They wanted to identify dormant threats before any damage occurred — a proactive threat hunting engagement.",
    challenge:
      "The existing SOC was reactive — alerts fired only after known signatures matched. The attacker had likely been inside the network for weeks before the near-miss. The team had no dedicated threat hunting methodology, behavioral baselines, or process for investigating suspicious lateral movement patterns.",
    solution:
      "We deployed a threat hunting framework using Velociraptor for endpoint telemetry collection and Elastic SIEM for correlation. Behavioral baselines were established per device class over a 2-week period. We created custom hunting hypotheses based on MITRE ATT&CK, focusing on persistence mechanisms, abnormal scheduled tasks, and unusual RDP connections. Each hypothesis was documented and tracked through to resolution or escalation.",
    techniques: [
      "Behavioral Anomaly Detection",
      "SIEM Log Analysis",
      "Persistence Mechanism Discovery",
      "Suspicious Lateral Movement Tracking",
    ],
    outcomeSummary:
      "Detected stealth intrusion before escalation through proactive log correlation and threat modeling.",
    technologies: [
      "Velociraptor (Endpoint Telemetry)",
      "Elastic SIEM",
      "MITRE ATT&CK Framework",
      "Sigma Rules (Detection)",
      "Jupyter Notebooks (Analysis)",
      "MISP (Threat Intelligence)",
    ],
    results:
      "Identified 3 dormant C2 communication channels, 2 unauthorized persistence mechanisms, and 1 instance of credential harvesting that had evaded all signature-based detection. All findings were remediated before any data encryption or exfiltration occurred. The client now runs quarterly threat hunting cycles.",
  },
  {
    slug: "deep-network-analysis",
    title: "Deep Network Traffic Analysis & Optimization",
    category: "Network Analysis",
    shortDesc:
      "Deep inspection of network traffic and architecture behavior for a telecom provider experiencing unexplained latency and intermittent drops.",
    image:
      "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=800",
    overview:
      "A regional telecom provider reported unexplained latency spikes and intermittent packet loss on their core backbone, affecting thousands of subscribers during peak hours. Standard monitoring tools showed no obvious issues.",
    challenge:
      "SNMP-based monitoring showed normal interface utilization, yet users reported degraded performance daily between 6-9 PM. Flow data was not granular enough to isolate the issue. The team needed deep packet inspection to understand exactly which traffic types were causing the bottlenecks, and whether the behavior indicated a security issue or a capacity problem.",
    solution:
      "We deployed a portable packet capture appliance at the core aggregation point and collected 48 hours of full PCAP data during the affected window. Using Wireshark and Zeek, we analyzed traffic flows, protocol distributions, and application-layer behavior. The analysis revealed a misconfigured load balancer causing asymmetric routing, combined with a P2P application consuming 40% of upstream bandwidth. We also identified an anomalous DNS tunneling pattern originating from a single workstation.",
    techniques: [
      "Packet Analysis (PCAP Review)",
      "Traffic Flow Mapping",
      "Protocol Inspection",
      "Bottleneck and Anomaly Detection",
    ],
    outcomeSummary:
      "Identified abnormal traffic patterns and optimized network performance while improving security posture.",
    technologies: [
      "Wireshark / tshark",
      "Zeek (formerly Bro)",
      "NFdump / NfSen",
      "Elasticsearch / Kibana (PCAP metadata)",
      "Python (flow analysis)",
      "Grafana (visualization)",
    ],
    results:
      "Resolved asymmetric routing — latency dropped from 180ms to 12ms. P2P traffic was throttled via QoS policies. The DNS tunneling incident was escalated to the security team, where it was confirmed as a data exfiltration attempt by an insider. Network capacity planning was revised based on flow data insights.",
  },
  {
    slug: "vulnerability-management-program",
    title: "Enterprise Vulnerability Management Program",
    category: "Vulnerability Management",
    shortDesc:
      "Identification, classification, and remediation of system vulnerabilities across a 2,500-employee enterprise with hybrid cloud infrastructure.",
    image:
      "https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=800",
    overview:
      "A multinational enterprise with 2,500 employees, 1,200 servers, and a hybrid AWS on-premise environment needed to establish a formal vulnerability management program after failing a compliance audit.",
    challenge:
      "There was no centralized vulnerability scanning — teams relied on sporadic manual checks. The audit revealed 4,000+ unpatched CVEs across the infrastructure, including 47 critical vulnerabilities that had been unpatched for over 6 months. There was no risk scoring methodology, no patch prioritization process, and no alignment with ISO 27001 or NIST frameworks.",
    solution:
      "We deployed Tenable Nessus for vulnerability scanning across all IP ranges and integrated it with AWS Security Hub for cloud coverage. Each finding was mapped to CVE, CVSS score, and relevant MITRE ATT&CK techniques. A risk scoring model (based on CVSS, exploitability, asset criticality, and exposure) was developed to prioritize remediation. We established a 30/60/90-day patch cadence with automated validation scans. A compliance dashboard mapped scan results to ISO 27001 and NIST CSF controls.",
    techniques: [
      "CVE Mapping and Risk Scoring",
      "Penetration Test Findings Summary",
      "Patch Management Strategy",
      "Compliance Alignment (ISO, NIST)",
    ],
    outcomeSummary:
      "Reduced system exposure by prioritizing and remediating critical vulnerabilities across infrastructure.",
    technologies: [
      "Tenable Nessus Professional",
      "AWS Security Hub",
      "CVSS / MITRE ATT&CK Mapping",
      "Qualys (Cloud Agents)",
      "Jira (Remediation Tracking)",
      "Power BI (Executive Dashboards)",
    ],
    results:
      "Critical vulnerabilities reduced from 47 to 2 within 90 days. Overall CVE count dropped from 4,200 to under 300. Passed ISO 27001 surveillance audit with zero non-conformities. Average patch deployment time decreased from 6 months to 14 days.",
  },
  {
    slug: "osint-threat-intelligence",
    title: "OSINT-Driven Threat Intelligence Gathering",
    category: "OSINT",
    shortDesc:
      "Gathering intelligence from publicly available sources to support threat attribution and risk assessment for a government contractor.",
    image:
      "https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=800",
    overview:
      "A government contractor handling classified communications infrastructure needed to assess the external threat landscape targeting their organization and supply chain partners.",
    challenge:
      "The client had no dedicated threat intelligence capability. They were aware of potential targeting by APT groups but had no visibility into leaked credentials, dark web mentions, social media chatter, or exposed infrastructure related to their organization. They needed an ethical, open-source intelligence gathering operation to identify exposures before adversaries could exploit them.",
    solution:
      "We conducted a targeted OSINT engagement across multiple public sources. Domain footprint analysis mapped all subdomains, exposed services, and SSL certificate metadata. Social media reconnaissance identified impersonation accounts and employee information leaks. Credential leak databases (HIBP, IntelX) were checked against corporate email domains. We also performed metadata analysis on publicly available documents to identify sensitive internal information exposure. Findings were compiled into a structured threat intelligence report with risk ratings and recommended countermeasures.",
    techniques: [
      "Social Media Reconnaissance",
      "Domain and Metadata Analysis",
      "Credential Leak Detection",
      "Threat Actor Profiling",
    ],
    outcomeSummary:
      "Collected actionable intelligence from open sources to support threat attribution and risk assessment.",
    technologies: [
      "theHarvester (Email/Domain OSINT)",
      "Shodan (Service Discovery)",
      "Maltego (Link Analysis)",
      "Have I Been Pwned (Credential Checks)",
      "SpiderFoot (Automated OSINT)",
      "ExifTool (Metadata Extraction)",
    ],
    results:
      "Identified 23 exposed subdomains with vulnerable services, 47 leaked employee credentials on public breach databases, 3 impersonation social media accounts, and 5 internal documents with exposed metadata on public repositories. All findings were remediated within 2 weeks, and the client established a continuous OSINT monitoring program.",
  },
  {
    slug: "isp-products-network-infrastructure",
    title: "ISP Products & Network Infrastructure",
    category: "ISP & Network Solutions",
    shortDesc:
      "Design, deployment, and management of ISP-grade infrastructure and connectivity solutions for businesses, institutions, and enterprise environments.",
    image:
      "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800",
    overview:
      "A multi-site enterprise client with 6 offices across the region required carrier-grade internet connectivity with centralized management, failover redundancy, and real-time NOC monitoring. Carium Tech was engaged to design and deploy a complete ISP-grade infrastructure as a managed service.",
    challenge:
      "Each office operated on separate consumer-grade broadband connections with no failover, no QoS, and no centralized visibility. Peak-hour congestion caused frequent drops in VoIP and video conferencing. CPE equipment was mismatched across sites with no standardized configuration, making troubleshooting slow and inconsistent. The client needed enterprise-grade reliability without building an internal ISP team.",
    solution:
      "We designed a three-tier ISP architecture with MikroTik CCR routers at the core, Cisco ISR 4300 series at distribution, and Ubiquiti airMAX devices as CPEs. A fiber backbone connected the primary sites with wireless PtP links as failover paths. QoS policies prioritized real-time traffic, and VLAN segmentation isolated client networks. A NOC stack using Grafana, The Dude, and LibrenMS provided real-time visibility into link health, bandwidth utilization, and device status across all sites.",
    techniques: [
      "Network Architecture Design",
      "Fiber & Wireless Connectivity",
      "Router & Firewall Configuration",
      "Bandwidth Management & QoS",
      "CPE Setup & Support",
      "Redundancy & Failover Design",
      "NOC-Style Monitoring & Uptime Optimization",
    ],
    outcomeSummary:
      "Designed and deployed scalable ISP infrastructure enabling high-availability internet connectivity across multiple client sites with 99.9% uptime.",
    technologies: [
      "MikroTik CCR1036 (Core)",
      "Cisco ISR 4321 (Distribution)",
      "Ubiquiti airMAX (CPE)",
      "GPON (Fiber Last-Mile)",
      "OSPF / BGP (Routing)",
      "Grafana / The Dude / LibrenMS (NOC)",
    ],
    results:
      "Achieved 99.96% uptime across all 6 sites over a 12-month period. Bandwidth utilization optimized from 92% to 55% during peak hours through QoS policies. NOC response time dropped from hours to under 5 minutes with real-time dashboards. Standardized CPE imaging reduced new site deployment from 3 days to 4 hours.",
  },
];

export default casesData;
